import React, { useEffect, useState, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import T from 'components/common/text/T';
import {
  sync_logo,
  picto_type_clinic,
  picto_type_lab,
  picto_type_milling,
  picto_type_designer,
  logo_dof,
} from 'components/base/images';
import { companyType } from 'lib/mapper';
import { useShallowAppSelector } from 'store/hooks';
import CustomSpan from 'components/common/text/CustomSpan';
import MuiButton from 'components/common/button/MuiButton';
import { beforeDash, color } from 'styles/utils';
import { Checkbox, FormControlLabel, FormGroup } from '@material-ui/core';
import MuiWrapper from 'components/common/input/MuiWrapper';
import useInput from 'lib/hooks/useInput';
import _ from 'lodash';
import cx from 'classnames';
import { UtilActions } from 'store/actionCreators';
import { useDidUpdateEffect } from 'lib/utils';
import { ILegalCategory, ILegalData } from 'lib/types/legalTypes';
import { Category } from '@material-ui/icons';
import DOMPurify from 'dompurify';
import { useTranslation } from 'react-i18next';

interface LegalAgreementProps {
  willSelectLeagal: boolean;
  isUserType: boolean;
  onSubmitUpdateUserType: (userType: Number) => void;
  onSubmitLegal: (legalArr: any[], userType: Number) => void;
  onCancelLegal: () => void;
}

function LegalAgreement({
  willSelectLeagal,
  isUserType,
  onSubmitUpdateUserType,
  onSubmitLegal,
  onCancelLegal,
}: LegalAgreementProps) {
  const {
    language,
    agreement,
    agreementSuccess,
    fetchLegalData: { data: legalData },
    legalDataSuccess,
    legalCategory: { list: legalCategoryList },
    legalCategoryListSuccess,
  }: {
    language: string;
    agreement: any;
    agreementSuccess: boolean;
    fetchLegalData: { data: ILegalData };
    legalDataSuccess: boolean;
    legalCategory: { list: ILegalCategory[] };
    legalCategoryListSuccess: boolean;
  } = useShallowAppSelector(state => ({
    language: state.base.language,
    agreement: state.util.agreementList.data,
    agreementSuccess: state.util.agreementList.success,
    fetchLegalData: state.util.legalData.data || {},
    legalDataSuccess: state.util.legalData.success,
    legalCategory: state.util.legalCategoryList.data || {},
    legalCategoryListSuccess: state.util.legalCategoryList.success,
  }));
  const { t } = useTranslation();
  const sanitizer = DOMPurify.sanitize;

  const [legalList, setLegalList] = useState<ILegalData[]>([]);
  const [legalTotalIndex, setLegalTotalIndex] = useState<number>(0);
  const [legalCurrIndex, setLegalCurrIndex] = useState<number>(0);
  const [validSubmit, setValidSubmit] = useState<boolean>(false);

  const type = useInput(0);

  type ObjType = {
    [index: string]: string;
    clinic: string;
    lab: string;
    milling: string;
    designer: string;
  };

  const typeIconArray: ObjType = {
    clinic: picto_type_clinic,
    lab: picto_type_lab,
    milling: picto_type_milling,
    designer: picto_type_designer,
  };

  useEffect(() => {
    UtilActions.fetch_legal_category_list_request();
  }, []);

  useDidUpdateEffect(() => {
    if (!legalCategoryListSuccess) return;
    console.log('HDS] legalCategoryList ________ ', legalCategoryList);
    if (legalCategoryList.length === 0) return;
    const convertCegalCategoryList: ILegalCategory[] = legalCategoryList.map(
      (item: ILegalCategory) => {
        return { ...item, isAgreement: !!item.isDefault ? false : true };
      },
    );

    console.log('HDs] convertCegalCategoryList ______ ', convertCegalCategoryList);
    setLegalList(convertCegalCategoryList);
    setLegalTotalIndex(legalCategoryList.length);
    handleFetchLegal(language, legalCategoryList[0].termsType);
  }, [legalCategoryListSuccess, language]);

  const handleFetchLegal = useCallback(
    (language: string, category: string) => {
      UtilActions.fetch_legal_data_request({
        language,
        category,
      });
      setLegalCurrIndex((draft: number) => draft + 1);
    },
    [legalCurrIndex],
  );

  useDidUpdateEffect(() => {
    if (!legalDataSuccess) return;
    console.log('HDS] legalData ________ ', legalData);
    setLegalList((draft: ILegalData[]) => {
      return draft.reduce((acc: any[], curr: any) => {
        if (!!legalData && curr.termsAgreementConfigIdx === legalData.termsAgreementConfigIdx) {
          return acc.concat({ ...curr, content: legalData.content, language: legalData.language });
        } else {
          return acc.concat(curr);
        }
      }, []);
    });
    if (legalTotalIndex <= legalCurrIndex) return;
    handleFetchLegal(language, legalCategoryList[legalCurrIndex].termsType);
  }, [legalDataSuccess, legalTotalIndex, legalCurrIndex]);

  useEffect(() => {
    console.log('HDS] LegalInfo _______ ', legalList);
  }, [legalList]);

  const hadleClickSubmitLegal = useCallback(() => {
    const submitData = legalList.map((legal: ILegalData) => {
      return {
        termsAgreementConfigIdx: legal.termsAgreementConfigIdx,
        isAgreement: !!legal.isAgreement ? 1 : 0,
      };
    });

    onSubmitLegal(submitData, type.value);
    console.log('HDS] hadleClickSubmitLegal ______ ', { ...submitData, ...type.value });
  }, [legalList, type.value]);

  const hadleClickSubmitUserType = () => {
    onSubmitUpdateUserType(type.value);
  };

  const handleChangeAgreementCheckbox = useCallback(
    (item: ILegalData) => {
      setLegalList((draft: ILegalData[]) => {
        return draft.reduce((acc: any[], curr: any) => {
          if (curr.termsAgreementConfigIdx === item.termsAgreementConfigIdx) {
            return acc.concat({ ...curr, isAgreement: !item.isAgreement });
          } else {
            return acc.concat(curr);
          }
        }, []);
      });
    },
    [legalList],
  );

  useEffect(() => {
    const agreementValid = legalList.find((item: ILegalData) => !item.isAgreement);
    if (!!agreementValid || type.value === 0) {
      setValidSubmit(false);
    } else {
      setValidSubmit(true);
    }
  }, [legalList, type.value]);

  return (
    <StyledLegalAgreement data-component-name="LegalAgreement">
      <div className="updateLegal__container legalContainer">
        <div className="legalContainer__wrapper">
          <div className="legalContainer__dof_logo_container">
            <img src={logo_dof} className="legalContainer__dof_logo" loading="lazy" />
          </div>
          <div className="legalContainer__legal_content_container">
            <div className="legalContainer__legal_description_box">
              <div className="legalContainer__legal_sync_logo"></div>
            </div>
            <div className="legalContainer__legal_content_list">
              {!!willSelectLeagal &&
                legalList.length > 0 &&
                legalList.map((legal: ILegalData) => {
                  if (!legal.isDefault) return;
                  return (
                    <div className="legalContainer__legal_content_wrapper ">
                      <div className="legalContainer__legal_content_title">
                        {/* <T>LEGAL_PRIVACY_POLICY</T> */}
                        {t(`LEGAL_TYPE_${legal.termsType}`)}
                      </div>
                      {!!legal?.content && (
                        <div className="legalContainer__legal_content_main">
                          <div className="legalContainer__legal_content_box">
                            <div
                              className="legalContainer__legal_content"
                              dangerouslySetInnerHTML={{ __html: sanitizer(legal.content) }}
                            ></div>
                          </div>
                        </div>
                      )}

                      <div className="legalContainer__legal_check_box">
                        <FormControlLabel
                          className="checkbox_control_label"
                          control={
                            <MuiWrapper>
                              <Checkbox
                                checked={!!legal.isAgreement}
                                value={!!legal.isAgreement}
                                onChange={() => {
                                  handleChangeAgreementCheckbox(legal);
                                }}
                                className="checkbox_control_checkbox"
                                name="policy"
                                color="primary"
                                icon={<div className="custom_checkbox"></div>}
                              />
                            </MuiWrapper>
                          }
                          label={
                            <CustomSpan fontSize={14} fontWeight={400} fontColor="#8f918f">
                              {/* <T>LEGAL_PRIVACY_AGREE</T> */}
                              {t(`LEGAL_AGREE_${legal.termsType}`)}
                            </CustomSpan>
                          }
                        />
                      </div>
                    </div>
                  );
                })}
            </div>

            {/* USER TYPE */}
            <div className="legalContainer__legal_content_wrapper user_type">
              <div className="legalContainer__legal_content_title">
                <T>LEGAL_USER_TYPE</T>
              </div>
              <div className="legalContainer__legal_content_subTitle">
                <CustomSpan fontSize={14} fontWeight={400} fontColor="#8f918f">
                  <T>LEGAL_USER_TYPE_TEXT</T>
                </CustomSpan>
              </div>
              <div className="legalContainer__legal_content_main">
                <div className="legalContainer__legal_userType_box type">
                  <FormGroup row className="type__form_group">
                    {_.map(companyType, (item, key) => {
                      if (item.id === 4) return;
                      const typeIndex = item.index;
                      const checked = item.id === Number(type.value);
                      const typeIcon = typeIconArray[item.index];

                      return (
                        <label
                          className={cx('type__form_checkbox', {
                            active: checked,
                          })}
                          key={key}
                        >
                          <input
                            type="checkbox"
                            className="type__form_checkbox_input"
                            name={typeIndex}
                            checked={checked}
                            onChange={() => {
                              if (item.id === type.value) {
                                type.onChange({ value: 0 });
                              } else {
                                type.onChange({ value: Number(item.id) });
                              }
                            }}
                          />
                          <span className="type__form_icon">
                            <img src={typeIcon} alt={key} />
                          </span>
                          <span className="type__form_name">
                            {typeIndex === 'clinic' && <T>GLOBAL_CLINIC</T>}
                            {typeIndex === 'lab' && <T>GLOBAL_LAB</T>}
                            {typeIndex === 'milling' && <T>GLOBAL_MILLING</T>}
                            {typeIndex === 'designer' && <T>GLOBAL_DESIGNER</T>}
                          </span>
                        </label>
                      );
                    })}
                  </FormGroup>
                </div>
              </div>
            </div>
          </div>
          <div className="legalContainer__button_container">
            <MuiButton
              className="legalContainer__button cancel"
              variant="outlined"
              color="default"
              disableElevation
              // disabled={!isValid}
              onClick={() => {
                onCancelLegal();
              }}
            >
              <CustomSpan fontSize={14}>
                <T>GLOBAL_CANCEL</T>
              </CustomSpan>
            </MuiButton>
            {!!willSelectLeagal ? (
              <MuiButton
                className="legalContainer__button submit"
                variant="contained"
                color="default"
                disableElevation
                // disabled={!isTerms || !isPrivace || type.value === 0}
                disabled={!validSubmit}
                onClick={() => {
                  hadleClickSubmitLegal();
                }}
              >
                <CustomSpan fontSize={14}>
                  <T>GLOBAL_ACCEPT</T>
                </CustomSpan>
              </MuiButton>
            ) : (
              <MuiButton
                className="legalContainer__button submit"
                variant="contained"
                color="default"
                disableElevation
                disabled={type.value === 0}
                onClick={() => {
                  hadleClickSubmitUserType();
                }}
              >
                <CustomSpan fontSize={14}>
                  <T>GLOBAL_SAVE</T>
                </CustomSpan>
              </MuiButton>
            )}
          </div>
        </div>
      </div>
    </StyledLegalAgreement>
  );
}

const StyledLegalAgreement = styled.section<{}>`
  min-width: 1904px;
  min-height: 925px;
  display: flex;
  z-index: 10002;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #f4f7fa;
  align-items: center;
  justify-content: center;
  /* font-size: 45px; */
  letter-spacing: 0.5px;
  margin-right: -16px;
  .legalContainer {
    .legalContainer__wrapper {
      width: 470px;
      margin: 35px 0;
      background-color: #ffffff;
      .legalContainer__dof_logo_container {
        width: 100%;
        height: 170px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-bottom: 2px solid rgb(241, 241, 241);
        .legalContainer__dof_logo {
          width: 200px;
        }
      }

      .legalContainer__legal_content_container {
        .legalContainer__legal_description_box {
          padding: 0 25px;
          .legalContainer__legal_sync_logo {
            text-align: center;
            /* padding: 40px 0; */
            height: 20px;
            .sync_logo {
              width: 130px;
              height: 40px;
            }
          }

          .legalContainer__legal_description_detail {
            p {
              width: 100%;
              word-break: break-all;
              color: #8f918f;
              font-size: 13px;
              &:first-child {
                color: #303030;
                margin-bottom: 20px;
              }
            }
          }
        }

        .legalContainer__legal_content_wrapper {
          &:nth-child(1) {
            margin-top: 0px;
          }
          margin-top: 40px;
          .legalContainer__legal_content_title {
            font-size: 24px;
            color: #303030;
            letter-spacing: -0ch.48px;
            ${beforeDash({ width: 45, backgroundColor: '#00a5e3' })};
          }

          .legalContainer__legal_content_subTitle {
            margin: 7px 0 0 0;
            ${beforeDash({ width: 45, backgroundColor: '#00a5e30' })};
          }

          .legalContainer__legal_content_main {
            padding: 20px 25px 0 25px;
            .legalContainer__legal_userType_box {
              width: 420px;
              height: 170px;
              overflow: hidden;
            }
            .legalContainer__legal_content_box {
              width: 420px;
              height: 170px;
              border: 1px solid #c4c4c4;
              border-radius: 3px;
              display: flex;
              align-items: center;
              justify-content: center;
              overflow: hidden;
              .legalContainer__legal_content {
                width: 410px;
                height: 150px;
                overflow: auto;
                .legalContainer__legal_content_iframe {
                  width: 100%;
                }
              }
            }
          }

          .legalContainer__legal_check_box {
            padding: 0 25px;
          }
        }
      }

      .legalContainer__button_container {
        padding: 60px 25px;
        display: flex;
        justify-content: space-between;
        border-top: 2px solid rgb(241, 241, 241);
        .legalContainer__button {
          width: 200px;
          height: 40px;
        }
      }

      .type__form_group {
        justify-content: space-between;
        margin-bottom: 50px;
        .type__form_checkbox {
          position: relative;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          align-items: center;
          width: 130px;
          height: 120px;
          border: 1px solid #bbb;
          border-radius: 5px;
          cursor: pointer;
          &.in_progress {
            background-color: #6c6c6c;
            border-color: #6c6c6c;

            .type__form_name {
              background-color: #6c6c6c;
            }

            img {
              opacity: 0.3;
            }
          }
          &.active {
            background-color: #f6fafc;
            border-color: ${color.blue};

            .type__form_name {
              background-color: ${color.blue};
            }
          }
          .type__form_checkbox_input {
            position: absolute;
            top: 0;
            left: 0;
            height: 0;
            width: 0;
            overflow: hidden;
          }
          .type__form_icon,
          .type__form_name {
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .type__form_icon {
            position: relative;
            display: flex;
            height: 94px;
            img {
              width: 100%;
              height: 68px;
            }
            p {
              position: absolute;
              font-size: 14px;
              color: #ffffff;
              width: 100%;
              height: 100%;
              text-align: center;
              top: 50%;
              font-weight: 600;
            }
          }
          .type__form_name {
            width: 100%;
            height: 26px;
            font-size: 12px;
            background-color: #bababa;
            text-align: center;
            color: #fff;
          }
        }
      }
    }
  }

  .checkbox_control_label {
    .checkbox_control_checkbox {
      width: 42px;
      height: 42px;
    }
  }

  .custom_checkbox {
    width: 20px;
    height: 20px;
    border: 1px solid #e5e7ed;
    border-radius: 4px;
  }
`;

export default React.memo(LegalAgreement);
